<template>
  <component
      :is="tag"
      v-bind="passAttrs"
      v-on="$listeners"
      :type="resolvedType"
      :class="pillClass"
      :title="resolvedTitle"
  >
    <slot />
  </component>
</template>

<script>
export default {
  name: "CTailwindPill",
  inheritAttrs: false,
  props: {
    tag: {
      type: String,
      default: "span",
    },
    type: {
      type: String,
      default: "button",
    },
    active: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "",
    },
  },
  computed: {
    passAttrs() {
      const rest = { ...this.$attrs };
      delete rest.class;
      delete rest.title;
      return rest;
    },
    pillClass() {
      return [
        "ctw-pill",
        this.active ? "ctw-pill--active" : "ctw-pill--inactive",
        this.$attrs.class,
      ];
    },
    resolvedType() {
      return this.tag === "button" ? this.type : undefined;
    },
    resolvedTitle() {
      if (this.title) return this.title;

      const slot = this.$slots.default || [];
      return slot
          .map((node) => (node.text || "").trim())
          .join("")
          .trim();
    },
  },
};
</script>

<style scoped>
.ctw-pill {
  box-sizing: border-box;
  display: inline-block; /* important for ellipsis on whole component */
  max-width: 115px; /* total pill width including padding */
  padding-inline: calc(var(--spacing) * 3);
  padding-block: calc(var(--spacing) * 1);

  font-family: var(--font-inter), var(--font-sans);
  font-size: var(--text-sm);
  line-height: 1.25rem;
  font-weight: var(--font-weight-medium);

  border-style: solid;
  border-width: 1px;
  border-radius: 9999px;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
  vertical-align: middle;

  transition-property: color, background-color, border-color, box-shadow, opacity;
  transition-duration: var(--default-transition-duration);
  transition-timing-function: var(--default-transition-timing-function);

}

.ctw-pill:focus {
  outline: none;
}

button.ctw-pill {
  cursor: pointer;
}

button.ctw-pill:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

/* Inactive */
.ctw-pill--inactive {
  border-color: var(--color-gray-200);
  background-color: var(--color-white);
  color: var(--color-gray-500);
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);

}

.ctw-pill--inactive:hover {
  border-color: var(--color-gray-300);
}

html.dark .ctw-pill--inactive {
  border-color: color-mix(in srgb, var(--color-gray-700) 60%, transparent);
  background-color: color-mix(in srgb, var(--color-gray-800) 60%, transparent);
  color: var(--color-gray-300);
}

html.dark .ctw-pill--inactive:hover {
  border-color: var(--color-gray-600);
}

/* Active */
.ctw-pill--active {
  border-color: transparent;
  background-color: var(--color-gray-900);
  color: var(--color-white);
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.12);
}

.ctw-pill--active:hover {
  border-color: transparent;
}

html.dark .ctw-pill--active {
  background-color: var(--color-gray-100);
  color: var(--color-gray-800);
}

html.dark .ctw-pill--active:hover {
  border-color: transparent;
}
</style>